import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* Left panel — 50% */}
      <div className="relative bg-[color:var(--bg)] grain">
        <div className="absolute inset-0 flex flex-col" style={{ paddingLeft: "12%", paddingRight: "12%" }}>
          {/* BT logo — bigger, per mockup ~200px wide */}
          <div style={{ marginTop: "13vh" }}>
            <img
              src="/bt-logo.webp"
              alt="Beyond Translation"
              className="w-[210px] h-auto object-contain"
              data-testid="login-bt-logo"
            />
          </div>

          {/* Insight logo — INSIGHT wordmark with orange lightbulb + v1.0 baked in */}
          <div style={{ marginTop: "14vh" }}>
            <span className="insight-logo-shine">
              <img
                src="/insight-logo.png"
                alt="Insight v1.0"
                className="w-[560px] h-auto object-contain block"
                data-testid="login-insight-logo"
              />
            </span>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            className="space-y-4"
            style={{ marginTop: "5vh", width: "100%", maxWidth: "540px" }}
            data-testid="login-form"
          >
            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] block mb-2">
                Email
              </label>
              <input
                type="email"
                required
                className="input-flat"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] block mb-2">
                Password
              </label>
              <input
                type="password"
                required
                className="input-flat"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
              />
            </div>

            {error && (
              <div
                className="border border-[color:var(--behind)] px-3 py-2 text-sm text-[color:var(--behind)]"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {submitting ? "Signing in…" : (<>Sign in <ArrowRight size={14} /></>)}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-auto pb-6 pt-8 text-center">
            <div className="mono text-[10px] text-[color:var(--ink-3)]" data-testid="login-footer">
              Developed by Geneza Solutions Private Limited
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — 50% */}
      <div
        className="hidden lg:block relative overflow-hidden"
        style={{
          backgroundColor: "#1c1a17",
          backgroundImage:
            'linear-gradient(180deg, rgba(28,26,23,0.35) 0%, rgba(28,26,23,0.70) 100%), url("https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 grain" />
        <div className="relative h-full flex flex-col justify-center p-16 lg:p-20 text-white z-10">
          <div className="mono text-[10px] uppercase tracking-widest text-white/70 mb-6">
            Region · Country · Cluster · Language
          </div>

          <div className="space-y-1 mb-10">
            <div className="serif text-6xl leading-[0.95] tracking-tight text-white">
              Spirit-Led.
            </div>
            <div className="serif text-6xl leading-[0.95] tracking-tight text-white">
              Tech-Driven.
            </div>
            <div className="serif text-6xl leading-[0.95] tracking-tight text-white">
              Data-Informed.
            </div>
          </div>

          <div className="serif text-2xl leading-snug tracking-tight max-w-xl text-white/90">
            &ldquo;And this gospel of the kingdom will be preached in the whole world
            as a testimony to all nations.&rdquo;
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-white/60 mt-4">
            Matthew 24:14
          </div>
        </div>
      </div>
    </div>
  );
}
